// =====================================================================================
// INVRT Radar - web console - the manual CSV upload client (story O05)
//
// The one frozen route this client calls is the HIGH-INTENT engine's authenticated batch
// ingestion, POST /v1/ingest/batch (src/high_intent/api/routes/ingest.py, integration
// branch, read-only reference). It is the SAME trusted-source machinery the Drive pull
// path drives: streaming sha256 registration exactly once, technical schema validation,
// and - with run=true, which this client always sends, exactly as the Drive path does -
// the backfill runner's own per-batch import, whose counters come back IN the 201 body.
//
//   TRUTH RULES. The import runs server-side inside the request: while the POST is in
//   flight the only honest progress state is "in progress", never a percentage. A 409
//   rejected_duplicate is the engine naming the batch this file already is. The engine
//   never attests anything: attestation stays a separate trusted-source operator step,
//   and this client renders none. The engine exposes no per-batch job-status GET on the
//   console surfaces, so post-registration state is read through the existing signed
//   org-delivery readiness surface (the batch's row in the campaign's source list) - the
//   panel polls that, and says so.
//
// TRANSPORT. Same origin law as the org-delivery client (hiDelivery.ts): the origin is
// named at build time by VITE_HI_API_URL with no code default, and a build without one
// fails closed as config_missing. The credential is the caller's Supabase bearer JWT -
// the engine's ordinary credential plane accepts it beside the x-api-key plane - and a
// browser must never hold an API key.
//
// PARSE LAW. Every response is parsed fail-closed against the frozen shapes and REFUSED
// when it names another campaign than the one requested - the console's own tenant
// guard. The engine's refusal bodies map to stable tokens: rejected_duplicate,
// rejected_schema, invalid_window_span, upload_too_large (413), forbidden (403),
// unauthenticated (401), ingest_import_failed (500 with a batch id).
// =====================================================================================

import { ApiError } from "@/lib/api";
import type { HiDeliveryContext } from "@/lib/hiDelivery";

/** The engine's own hard body limit (ingest.py MAX_INGEST_BODY_BYTES), mirrored for the
 * panel's pre-flight so an oversized file is refused before a byte is sent. */
export const INGEST_MAX_BODY_BYTES = 50 * 1024 * 1024;

/** The per-reason dispositions the 201 imported body carries (batch_manifest's own
 * counters, read by _run_imported_batch after the runner finalised the batch). */
export interface IngestCounters {
  parsedRecords: number;
  newLeads: number;
  rejectedQuality: number;
  reIntentCounted: number;
  reDeliveriesNotCounted: number;
  invalid: number;
  quarantined: number;
}

export interface IngestBatchResult {
  batchId: string;
  campaignKey: string;
  fileName: string;
  sha256: string;
  window: { start: string; end: string };
  sequenceNo: number;
  status: "received" | "imported";
  /** The engine's counters when status is imported; always null for received. */
  counters: IngestCounters | null;
}

// ---------------------------------------------------------------------------------------
// Fail-closed parsing (hiDelivery.ts's discipline, ingest.py's frozen shapes)
// ---------------------------------------------------------------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;

function badContract(message: string): ApiError {
  return new ApiError({
    token: "internal_error",
    message,
    httpStatus: 502,
    correlationId: null,
  });
}

const BAD_CONTRACT = "The ingest response did not match the frozen contract.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function reqString(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) throw badContract(BAD_CONTRACT);
  return value;
}

function reqCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw badContract(BAD_CONTRACT);
  return value;
}

const COUNTER_KEYS = [
  "parsed_records",
  "new_lead_count",
  "rejected_quality_count",
  "re_intent_count",
  "re_delivery_count",
  "invalid_count",
  "quarantined_count",
] as const;

function parseCounters(value: unknown): IngestCounters {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  if (Object.keys(value).length !== COUNTER_KEYS.length) throw badContract(BAD_CONTRACT);
  return {
    parsedRecords: reqCount(value.parsed_records),
    newLeads: reqCount(value.new_lead_count),
    rejectedQuality: reqCount(value.rejected_quality_count),
    reIntentCounted: reqCount(value.re_intent_count),
    reDeliveriesNotCounted: reqCount(value.re_delivery_count),
    invalid: reqCount(value.invalid_count),
    quarantined: reqCount(value.quarantined_count),
  };
}

/** The frozen 201 body: the batch identity, and the counters only when the engine
 * reports status imported. The tenant guard refuses any other campaign's body. */
export function parseIngestBatchResponse(value: unknown, campaignKey: string): IngestBatchResult {
  if (!isRecord(value)) throw badContract(BAD_CONTRACT);
  if (value.campaign_key !== campaignKey) {
    throw new ApiError({
      token: "tenant_mismatch",
      message: "The ingest response named a different campaign. Nothing from another campaign is shown.",
      httpStatus: 502,
      correlationId: null,
    });
  }
  const batchId = reqString(value.batch_id);
  if (!UUID_PATTERN.test(batchId)) throw badContract(BAD_CONTRACT);
  const sha256 = reqString(value.sha256);
  if (!SHA256_PATTERN.test(sha256)) throw badContract(BAD_CONTRACT);
  const status = value.status;
  if (status !== "received" && status !== "imported") throw badContract(BAD_CONTRACT);
  if (!isRecord(value.window)) throw badContract(BAD_CONTRACT);
  const sequenceRaw = value.sequence_no;
  if (typeof sequenceRaw !== "number" || !Number.isInteger(sequenceRaw) || sequenceRaw < 0) {
    throw badContract(BAD_CONTRACT);
  }
  const counters =
    status === "imported"
      ? parseCounters(value.counters)
      : value.counters === undefined || value.counters === null
        ? null
        : (() => {
            throw badContract(BAD_CONTRACT);
          })();
  return {
    batchId,
    campaignKey,
    fileName: reqString(value.file_name),
    sha256,
    window: { start: reqString(value.window.start), end: reqString(value.window.end) },
    sequenceNo: sequenceRaw,
    status,
    counters,
  };
}

// ---------------------------------------------------------------------------------------
// The call and the refusal mapping (ingest.py's own error envelope)
// ---------------------------------------------------------------------------------------

function isRecord2(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Turns one of the engine's ingest refusal bodies into the stable token the UI branches
 * on, carrying the engine's own fields (the existing batch id, the schema reason) in
 * details so the surface can render the engine's truth instead of paraphrasing it. */
function toIngestError(response: Response, parsedBody: unknown): ApiError {
  const body = isRecord2(parsedBody) ? parsedBody : null;
  const headerCorrelationId = response.headers.get("X-Correlation-Id");
  const correlationId =
    body !== null && typeof body.correlation_id === "string" ? body.correlation_id : headerCorrelationId;
  const errorToken = body !== null && typeof body.error === "string" ? body.error : null;
  const detail = body?.detail;

  if (response.status === 409 && errorToken === "rejected_duplicate") {
    return new ApiError({
      token: "rejected_duplicate",
      message: typeof detail === "string" ? detail : "The submitted content is already registered.",
      httpStatus: 409,
      correlationId,
      details: {
        ...(typeof body?.sha256 === "string" ? { sha256: body.sha256 } : {}),
        ...(typeof body?.batch_id === "string" ? { batch_id: body.batch_id } : {}),
        ...(typeof detail === "string" ? { detail } : {}),
      },
    });
  }
  if (response.status === 422 && errorToken === "rejected_schema") {
    return new ApiError({
      token: "rejected_schema",
      message: "The engine refused the file on technical validation.",
      httpStatus: 422,
      correlationId,
      details: {
        ...(typeof detail === "string" ? { reason: detail } : {}),
        ...(typeof body?.batch_id === "string" ? { batch_id: body.batch_id } : {}),
      },
    });
  }
  if (response.status === 422 && errorToken === "invalid_window_span") {
    return new ApiError({
      token: "invalid_window_span",
      message: "The engine refused the batch window.",
      httpStatus: 422,
      correlationId,
    });
  }
  if (response.status === 422) {
    return new ApiError({
      token: "validation_failed",
      message: "The engine refused the request as malformed.",
      httpStatus: 422,
      correlationId,
    });
  }
  if (response.status === 413) {
    return new ApiError({
      token: "upload_too_large",
      message: "The file is larger than the engine accepts.",
      httpStatus: 413,
      correlationId,
    });
  }
  if (response.status === 403) {
    return new ApiError({
      token: "forbidden",
      message: "Access denied to this campaign.",
      httpStatus: 403,
      correlationId,
    });
  }
  if (response.status === 401) {
    return new ApiError({
      token: "unauthenticated",
      message: "There is no active session.",
      httpStatus: 401,
      correlationId,
    });
  }
  if (response.status === 500 && errorToken === "ingest_import_failed") {
    return new ApiError({
      token: "ingest_import_failed",
      message: "The engine registered the batch but the import failed.",
      httpStatus: 500,
      correlationId,
      details: {
        ...(typeof body?.batch_id === "string" ? { batch_id: body.batch_id } : {}),
        ...(typeof body?.state === "string" ? { state: body.state } : {}),
      },
    });
  }
  return new ApiError({
    token: response.status >= 500 ? "provider_unavailable" : "internal_error",
    message: "The ingest service refused or could not answer this request.",
    httpStatus: response.status,
    correlationId,
  });
}

/** POST /v1/ingest/batch?campaign_key=...&run=true with the file's raw bytes - the Drive
 * path's own register-then-import step. The caller's bearer JWT is the credential; the
 * file name rides the X-Filename header (the engine reduces it to a bare name itself). */
export async function uploadBatch(
  ctx: HiDeliveryContext,
  campaignKey: string,
  file: File,
): Promise<IngestBatchResult> {
  const doFetch = ctx.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await doFetch(
      `${ctx.origin}/v1/ingest/batch?campaign_key=${encodeURIComponent(campaignKey)}&run=true`,
      {
        method: "POST",
        headers: {
          ...(ctx.accessToken === null ? {} : { Authorization: `Bearer ${ctx.accessToken}` }),
          "X-Filename": file.name,
          "Content-Type": "text/csv",
          Accept: "application/json",
        },
        body: file,
        ...(ctx.signal === undefined ? {} : { signal: ctx.signal }),
      },
    );
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError({
      token: "provider_unavailable",
      message: "The ingest service could not be reached. Nothing was uploaded.",
      httpStatus: 502,
      correlationId: null,
    });
  }
  if (response.ok) {
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      throw badContract(BAD_CONTRACT);
    }
    return parseIngestBatchResponse(parsed, campaignKey);
  }
  let parsedBody: unknown = null;
  try {
    parsedBody = await response.json();
  } catch {
    parsedBody = null;
  }
  throw toIngestError(response, parsedBody);
}
